import React from "react";
import { useTranslation, Trans } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { PartnerFeatureEnum } from "@/__generated__/types";

interface FeatureDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  type: "feature" | "permission";
  feature: PartnerFeatureEnum | null;
}

export const FeatureDialog = ({
  isOpen,
  onOpenChange,
  type,
  feature,
}: FeatureDialogProps) => {
  const { t } = useTranslation();

  const getFeatureLabel = (featureEnum: PartnerFeatureEnum | null): string => {
    if (!featureEnum) return "";

    switch (featureEnum) {
      case PartnerFeatureEnum.BulkMessaging:
        return t("global.features.bulkMessaging");
      case PartnerFeatureEnum.Games:
        return t("global.features.automatedGames");
      case PartnerFeatureEnum.Rise:
        return t("global.features.RISE");
      default:
        return String(featureEnum).toLowerCase();
    }
  };

  const featureLabel = getFeatureLabel(feature);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="flex min-h-[323px] flex-col rounded-lg border p-6">
        <DialogHeader>
          <DialogTitle>
            {type === "feature"
              ? t("upgrade.features.title", {
                  feature: featureLabel,
                })
              : t("upgrade.permission.title")}
          </DialogTitle>
          <DialogDescription className="py-4">
            {type === "feature" ? (
              <Trans
                i18nKey="upgrade.features.message"
                values={{ feature: featureLabel }}
                components={{
                  supportLink: (
                    <a
                      href="mailto:support@zeemee.com"
                      className="text-blue-primary hover:underline"
                    />
                  ),
                }}
              />
            ) : (
              <Trans
                i18nKey="upgrade.permission.message"
                components={{
                  supportLink: (
                    <a
                      href="mailto:support@zeemee.com"
                      className="text-blue-primary hover:underline"
                    />
                  ),
                }}
              />
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-auto">
          <DialogClose asChild>
            <Button>{t("global.close")}</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
